export const ErrorCode = {
  NETWORK: 'NETWORK',
  NOT_FOUND: 'NOT_FOUND',
  ACCOUNT_NOT_FOUND: 'ACCOUNT_NOT_FOUND',
  UNAUTHORIZED: 'UNAUTHORIZED',
  RATE_LIMITED: 'RATE_LIMITED',
  SERVER: 'SERVER',
  VALIDATION: 'VALIDATION',
  UNKNOWN: 'UNKNOWN',
} as const;

export type ErrorCode = (typeof ErrorCode)[keyof typeof ErrorCode];

export interface AppError {
  code: ErrorCode;
  detail?: string;
}

interface ApiErrorShape {
  response?: {
    status?: number;
    data?: { detail?: string; message?: string };
  };
  message?: string;
}

const STATUS_CODE_MAP: Record<number, ErrorCode> = {
  401: ErrorCode.UNAUTHORIZED,
  403: ErrorCode.UNAUTHORIZED,
  404: ErrorCode.NOT_FOUND,
  422: ErrorCode.VALIDATION,
  429: ErrorCode.RATE_LIMITED,
};

const isAccountNotFound = (text: string): boolean => /not found|no customer found/i.test(text);

const isNetworkError = (text: string): boolean => /network error|failed to fetch|fetch/i.test(text);

const classify = (err: ApiErrorShape): ErrorCode => {
  const status = err.response?.status;
  const detail = err.response?.data?.detail ?? err.response?.data?.message ?? '';
  const message = err.message ?? '';
  const haystack = `${detail} ${message}`;

  if (isAccountNotFound(haystack)) return ErrorCode.ACCOUNT_NOT_FOUND;
  if (status && STATUS_CODE_MAP[status]) return STATUS_CODE_MAP[status];
  if (status && status >= 500) return ErrorCode.SERVER;
  if (isNetworkError(message)) return ErrorCode.NETWORK;

  return ErrorCode.UNKNOWN;
};

export const parseApiError = (err: unknown): AppError => {
  const e = (err ?? {}) as ApiErrorShape;
  return {
    code: classify(e),
    detail: e.response?.data?.detail ?? e.response?.data?.message,
  };
};
